import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapTrifold, CaretLeft, CaretRight, GearSix, Path, Truck, WhatsappLogo, FilePdf, LinkSimple, Trash, ArrowUp, ArrowDown, Warning, MapPin, Plus, CheckCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import API from '@/lib/api';
import { isIOS as isIOSPlatform } from '@/lib/platform';
import RouteKaart from '@/components/RouteKaart';

const vandaagISO = () => new Date().toISOString().slice(0, 10);
const schuifDag = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDatum = (iso) => { try { return new Date(`${iso}T12:00:00`).toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return iso; } };
const fmtDuur = (s) => { const m = Math.round((s || 0) / 60); return `${Math.floor(m / 60)}u ${String(m % 60).padStart(2, '0')}`; };
const fmtKm = (m) => `${((m || 0) / 1000).toFixed(1)} km`;
const RIT_STATUS = { gepland: ['Gepland', 'bg-blue-100 text-blue-800'], onderweg: ['Onderweg', 'bg-cyan-100 text-cyan-800'], afgewerkt: ['Afgewerkt', 'bg-emerald-100 text-emerald-800'] };

const Lbl = ({ children }) => <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-1">{children}</label>;

const StopRij = ({ s, gekozen, toggle, onHergeocode }) => (
  <div className={`flex items-start gap-2 px-3 py-2 border-b border-gray-100 ${gekozen ? '' : 'opacity-50'}`} data-testid={`route-stop-${s.doc_id}`}>
    <input type="checkbox" className="mt-1 w-4 h-4 accent-[#F59E0B]" checked={gekozen} onChange={toggle} data-testid={`route-stop-check-${s.doc_id}`} />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-sm text-gray-900 truncate">{s.klant_naam || '—'}</span>
        <Link to={`/documenten/${s.doc_id}/bewerk`} className="font-mono text-[11px] text-[#002FA7] hover:underline shrink-0">{s.documentnr}</Link>
      </div>
      <div className="text-[11px] text-gray-600 truncate" title={s.adres?.tekst}>{s.adres?.tekst || <span className="text-red-600">Geen adres</span>}</div>
      <div className="text-[10px] text-gray-400 flex items-center gap-2 mt-0.5">
        {s.gewicht_kg > 0 && <span>{s.gewicht_kg} kg</span>}
        <span>{s.aantal_lijnen} lijn(en)</span>
        {s.leverdatum && <span>lever {s.leverdatum.split('-').reverse().join('/')}</span>}
        {s.geo ? <span className="text-emerald-700 inline-flex items-center gap-0.5"><MapPin size={11} weight="fill" /> op kaart</span>
          : <button type="button" className="text-amber-700 inline-flex items-center gap-0.5 hover:underline" onClick={onHergeocode} data-testid={`route-stop-geo-${s.doc_id}`}><Warning size={11} weight="fill" /> adres niet gevonden — opnieuw</button>}
      </div>
    </div>
  </div>
);

const RitKaart = ({ rit, live, actief, kandidaten = [], bezig, onToon, onPdf, onWhatsApp, onLink, onVerwijder, onStopStatus, onBijzetten }) => {
  const [open, setOpen] = useState(false);
  const [st, cls] = RIT_STATUS[live?.status || rit.status] || [rit.status, 'bg-gray-100 text-gray-700'];
  const geleverd = rit.stops.filter(s => s.status === 'geleverd').length;
  const pos = live?.positie || rit.laatste_positie;
  const minGeleden = pos?.ts ? Math.max(0, Math.round((Date.now() - new Date(pos.ts).getTime()) / 60000)) : null;
  const trackingLive = (live ? live.tracking_actief : rit.tracking_actief) && minGeleden != null && minGeleden < 5;
  const vertrokken = live?.vertrokken_at || rit.vertrokken_at;
  const beeindigd = live?.beeindigd_at || rit.beeindigd_at;
  return (
    <div className={`border ${actief ? 'border-[#F59E0B] ring-1 ring-[#F59E0B]' : 'border-gray-200'} bg-white`} data-testid={`rit-kaart-${rit.id}`}>
      <button type="button" className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-gray-50" onClick={onToon} data-testid={`rit-toon-${rit.id}`}>
        <Truck size={18} weight="duotone" className="text-[#F59E0B] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">{rit.chauffeur_naam || 'Zonder chauffeur'} {rit.nummerplaat && <span className="font-mono text-[10px] text-gray-500">{rit.nummerplaat}</span>}</div>
          <div className="text-[11px] text-gray-500">{rit.stops.length} stops · {fmtKm(rit.totaal_afstand_m)} · {rit.vertrek_uur} → {rit.eind_uur} · {geleverd}/{rit.stops.length} geleverd</div>
        </div>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 ${cls}`} data-testid={`rit-status-${rit.id}`}>{st}</span>
      </button>
      {(vertrokken || pos) && (
        <div className={`mx-3 mb-2 px-2 py-1.5 text-[11px] border ${trackingLive ? 'bg-cyan-50 border-cyan-200 text-cyan-900' : 'bg-gray-50 border-gray-200 text-gray-600'}`} data-testid={`rit-live-${rit.id}`}>
          <span className="font-bold">{trackingLive ? '● Live' : beeindigd ? '■ Rit beëindigd' : '○ Geen recente positie'}</span>
          {vertrokken && <span> · vertrokken {new Date(vertrokken).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</span>}
          {beeindigd && <span> · einde {new Date(beeindigd).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</span>}
          {pos && <span> · positie {minGeleden === 0 ? 'zonet' : `${minGeleden} min geleden`}{pos.snelheid_kmh != null && trackingLive ? ` · ${Math.round(pos.snelheid_kmh)} km/u` : ''}</span>}
          {live?.volgende_stop && !beeindigd && <div className="text-[10px] mt-0.5">Volgende: #{live.volgende_stop.positie} {live.volgende_stop.klant_naam} · ETA {live.volgende_stop.eta}</div>}
        </div>
      )}
      <div className="flex flex-wrap gap-1 px-3 pb-2">
        <button type="button" className="text-[11px] font-semibold px-2 py-1 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1" onClick={onPdf} data-testid={`rit-pdf-${rit.id}`}><FilePdf size={13} /> Ritblad PDF</button>
        <button type="button" className="text-[11px] font-semibold px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1" onClick={onWhatsApp} data-testid={`rit-whatsapp-${rit.id}`}><WhatsappLogo size={13} weight="fill" /> WhatsApp</button>
        <button type="button" className="text-[11px] font-semibold px-2 py-1 border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1" onClick={onLink} data-testid={`rit-link-${rit.id}`}><LinkSimple size={13} /> Chauffeur-link</button>
        <button type="button" className="text-[11px] font-semibold px-2 py-1 border border-gray-300 hover:bg-gray-50" onClick={() => setOpen(o => !o)} data-testid={`rit-stops-toggle-${rit.id}`}>{open ? 'Stops ▲' : 'Stops ▼'}</button>
        <button type="button" className="ml-auto text-[11px] font-semibold px-2 py-1 text-red-600 hover:bg-red-50 inline-flex items-center gap-1" onClick={onVerwijder} data-testid={`rit-verwijder-${rit.id}`}><Trash size={13} /> Annuleer rit</button>
      </div>
      <div className="px-3 pb-2 flex items-center gap-2" data-testid={`rit-bijzet-rij-${rit.id}`}>
        <Plus size={14} className="text-emerald-600 shrink-0" />
        {kandidaten.length ? (
          <select className="field-input text-xs flex-1 h-9" value="" disabled={bezig === 'bijzet'} onChange={e => e.target.value && onBijzetten(e.target.value)} data-testid={`rit-bijzet-${rit.id}`}>
            <option value="">{bezig === 'bijzet' ? 'Bijzetten…' : `Leverbon bijzetten in deze rit… (${kandidaten.length})`}</option>
            {kandidaten.map(k => <option key={k.doc_id} value={k.doc_id}>{k.documentnr} · {k.klant_naam}{k.status ? ` · ${k.status}` : ''}{k.leverdatum ? ` · lever ${k.leverdatum.split('-').reverse().join('/')}` : ''}</option>)}
          </select>
        ) : <span className="text-[11px] text-gray-400">Geen open leverbon meer om bij te zetten</span>}
      </div>
      <div className="px-3 pb-2 text-[10px] text-gray-500">Ook een leverbon die al verzonden is maar nog niet gefactureerd kan je hier nadien nog bijzetten — ze komt automatisch op de slimste plek en de chauffeur ziet ze meteen.</div>
      {open && (
        <div className="border-t border-gray-100" data-testid={`rit-stops-${rit.id}`}>
          {rit.stops.map(s => (
            <div key={s.doc_id} className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-50 text-[11px]">
              <span className="w-5 h-5 rounded-full bg-gray-800 text-white font-bold flex items-center justify-center text-[10px] shrink-0">{s.positie}</span>
              <span className="font-mono text-gray-500 w-10 shrink-0">{s.eta}</span>
              <span className="flex-1 truncate"><b>{s.klant_naam}</b> · {s.adres?.tekst} · <span className="font-mono">{s.documentnr}</span></span>
              {s.status === 'geleverd' ? (
                <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> geleverd
                  <button type="button" className="text-gray-400 hover:text-gray-700" title="Terug naar open" onClick={() => onStopStatus(s.doc_id, 'open')}><ArrowCounterClockwise size={12} /></button></span>
              ) : s.status === 'overgeslagen' ? <span className="text-amber-700">overgeslagen</span> : (
                <button type="button" className="text-emerald-700 font-semibold hover:underline" onClick={() => onStopStatus(s.doc_id, 'geleverd')} data-testid={`rit-stop-geleverd-${rit.id}-${s.doc_id}`}>✓ Geleverd</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InstellingenPaneel = ({ inst, onOpslaan, onSluit }) => {
  const [f, setF] = useState({ ...inst.depot, vertrek_uur: inst.vertrek_uur, stoptijd_min: inst.stoptijd_min, roundtrip: inst.roundtrip });
  const [busy, setBusy] = useState(false);
  const u = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  return (
    <div className="border border-gray-200 bg-white p-4 mb-4" data-testid="route-instellingen">
      <div className="text-sm font-bold text-gray-900 mb-3 inline-flex items-center gap-2"><GearSix size={16} /> Instellingen routeplanner</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="col-span-2"><Lbl>Vertrekpunt — straat</Lbl><input className="field-input" value={f.straat || ''} onChange={u('straat')} data-testid="route-inst-straat" /></div>
        <div><Lbl>Nr</Lbl><input className="field-input" value={f.nr || ''} onChange={u('nr')} data-testid="route-inst-nr" /></div>
        <div><Lbl>Postcode</Lbl><input className="field-input" value={f.postcode || ''} onChange={u('postcode')} data-testid="route-inst-postcode" /></div>
        <div><Lbl>Gemeente</Lbl><input className="field-input" value={f.stad || ''} onChange={u('stad')} data-testid="route-inst-stad" /></div>
        <div><Lbl>Land</Lbl><input className="field-input" value={f.land || 'BE'} onChange={u('land')} maxLength={2} data-testid="route-inst-land" /></div>
        <div><Lbl>Standaard vertrekuur</Lbl><input type="time" className="field-input" value={f.vertrek_uur || '08:00'} onChange={u('vertrek_uur')} data-testid="route-inst-vertrek" /></div>
        <div><Lbl>Stoptijd per levering (min)</Lbl><input type="number" min="0" max="180" className="field-input" value={f.stoptijd_min} onChange={u('stoptijd_min')} data-testid="route-inst-stoptijd" /></div>
        <div className="col-span-2 flex items-end"><label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={f.roundtrip !== false} onChange={e => setF(p => ({ ...p, roundtrip: e.target.checked }))} data-testid="route-inst-roundtrip" /> Standaard terug naar vertrekpunt</label></div>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button" className="btn btn-primary" disabled={busy} data-testid="route-inst-opslaan" onClick={async () => { setBusy(true); try { await onOpslaan({ depot: { straat: f.straat, nr: f.nr, postcode: f.postcode, stad: f.stad, land: f.land }, vertrek_uur: f.vertrek_uur, stoptijd_min: parseInt(f.stoptijd_min, 10) || 0, roundtrip: f.roundtrip !== false }); } finally { setBusy(false); } }}>{busy ? 'Opslaan…' : 'Opslaan'}</button>
        <button type="button" className="btn" onClick={onSluit}>Sluiten</button>
      </div>
    </div>
  );
};

export default function RoutePlanningPage() {
  const [datum, setDatum] = useState(vandaagISO());
  const [data, setData] = useState({ stops: [], ritten: [], andere_open: [], instellingen: null });
  const [chauffeurs, setChauffeurs] = useState([]);
  const [gekozen, setGekozen] = useState(new Set());
  const [form, setForm] = useState({ chauffeurId: '', vertrekUur: '08:00', stoptijd: 15, roundtrip: true });
  const [planning, setPlanning] = useState(null);
  const [actieveRit, setActieveRit] = useState(null);
  const [bezig, setBezig] = useState('');
  const [geoProgress, setGeoProgress] = useState(null);
  const [instOpen, setInstOpen] = useState(false);
  const [formInit, setFormInit] = useState(false);
  const [bijvoeg, setBijvoeg] = useState('');
  const [live, setLive] = useState({ voertuigen: [] });
  const [spoor, setSpoor] = useState([]);
  const [kaartView, setKaartView] = useState(null);

  // Live wagens: elke 20s verversen (ritten met GPS-positie van de chauffeur-telefoon)
  useEffect(() => {
    let stop = false;
    const haal = () => API.get(`/routes/live?datum=${datum}`).then(r => { if (!stop) setLive(r.data); }).catch(() => {});
    haal();
    const t = setInterval(haal, 20000);
    return () => { stop = true; clearInterval(t); };
  }, [datum]);
  useEffect(() => {
    if (!actieveRit) { setSpoor([]); return undefined; }
    let stop = false;
    const haal = () => API.get(`/routes/ritten/${actieveRit.id}/spoor`).then(r => { if (!stop) setSpoor((r.data.punten || []).map(p => [p.lat, p.lon])); }).catch(() => {});
    haal();
    const t = setInterval(haal, 20000);
    return () => { stop = true; clearInterval(t); };
  }, [actieveRit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mergeStops = useCallback((nieuwe) => setData(d => ({ ...d, stops: d.stops.map(s => nieuwe.find(n => n.doc_id === s.doc_id) || s) })), []);

  const geocodeer = useCallback(async (ids, metDepot = true) => {
    if (!ids.length && !metDepot) return;
    setBezig('geocode');
    let done = 0;
    const total = ids.length;
    setGeoProgress({ done, total });
    try {
      for (let i = 0; i < 40; i++) {
        const r = await API.post('/routes/geocode', { doc_ids: ids, depot: metDepot });
        mergeStops(r.data.stops || []);
        if (r.data.depot_geo) setData(d => ({ ...d, instellingen: d.instellingen ? { ...d.instellingen, depot_geo: r.data.depot_geo } : d.instellingen }));
        done += r.data.gedaan || 0;
        setGeoProgress({ done, total });
        const depotFout = (r.data.fouten || []).find(f => f.depot);
        if (depotFout) toast.error(depotFout.detail || 'Vertrekadres niet gevonden');
        if (!r.data.resterend || !r.data.gedaan) break;
      }
    } catch (e) {
      toast.error('Adressen opzoeken mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setBezig(''); setGeoProgress(null); }
  }, [mergeStops]);

  const laad = useCallback(async (d) => {
    setBezig('laden');
    try {
      const r = await API.get(`/routes/dag?datum=${d}`);
      setData(r.data);
      setGekozen(new Set((r.data.stops || []).map(s => s.doc_id)));
      setPlanning(null);
      setActieveRit(null);
      if (!formInit && r.data.instellingen) {
        setForm(f => ({ ...f, vertrekUur: r.data.instellingen.vertrek_uur, stoptijd: r.data.instellingen.stoptijd_min, roundtrip: r.data.instellingen.roundtrip }));
        setFormInit(true);
      }
      const missing = (r.data.stops || []).filter(s => !s.geo).map(s => s.doc_id);
      if (missing.length || !r.data.instellingen?.depot_geo) geocodeer(missing, !r.data.instellingen?.depot_geo);
      else setBezig('');
    } catch (e) {
      toast.error('Laden mislukt: ' + (e.response?.data?.detail || e.message));
      setBezig('');
    }
  }, [formInit, geocodeer]);

  useEffect(() => { laad(datum); }, [datum]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { API.get('/chauffeurs').then(r => setChauffeurs((r.data || []).filter(c => c.actief !== false))).catch(() => {}); }, []);

  const toggle = (id) => setGekozen(g => { const n = new Set(g); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const gekozenStops = data.stops.filter(s => gekozen.has(s.doc_id));

  const optimaliseer = async (ids = null, vaste = false) => {
    const docIds = ids || gekozenStops.map(s => s.doc_id);
    if (!docIds.length) { toast.error('Vink minstens 1 levering aan'); return; }
    setBezig('optimaliseer');
    try {
      const r = await API.post('/routes/optimaliseer', { datum, doc_ids: docIds, vertrek_uur: form.vertrekUur, stoptijd_min: form.stoptijd, roundtrip: form.roundtrip, vaste_volgorde: vaste });
      setPlanning(r.data);
      setActieveRit(null);
      if (!vaste) toast.success(`Route berekend: ${r.data.stops.length} stops · ${fmtKm(r.data.totaal_afstand_m)} · ${fmtDuur(r.data.totaal_duur_s)}`);
    } catch (e) {
      toast.error('Optimaliseren mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setBezig(''); }
  };
  const verplaats = (idx, dir) => {
    const ids = planning.stops.map(s => s.doc_id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    optimaliseer(ids, true);
  };
  const haalUitPlanning = (docId) => {
    const ids = planning.stops.map(s => s.doc_id).filter(i => i !== docId);
    setGekozen(g => { const n = new Set(g); n.delete(docId); return n; });
    if (ids.length) optimaliseer(ids, true); else setPlanning(null);
  };
  const vastleggen = async () => {
    if (!planning) return;
    setBezig('opslaan');
    try {
      const ch = chauffeurs.find(c => c.id === form.chauffeurId);
      const r = await API.post('/routes/ritten', { datum, chauffeur_id: form.chauffeurId || '', chauffeur_naam: ch?.naam || '', planning });
      toast.success(`Rit vastgelegd — ${r.data.stops.length} leverbonnen staan nu op "onderweg"`);
      await laad(datum);
      setActieveRit(r.data);
    } catch (e) {
      toast.error('Vastleggen mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setBezig(''); }
  };
  const openUrl = (url) => { const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove(); };
  const ritPdf = async (rit) => {
    try {
      // Kaart afdrukken zoals ze op het scherm staat (enkel voor de rit die op de kaart staat)
      const v = kaartView && (!actieveRit || actieveRit.id === rit.id)
        ? `&kaart_lat=${kaartView.lat}&kaart_lon=${kaartView.lon}&kaart_zoom=${kaartView.zoom}&kaart_b=${kaartView.breedte}&kaart_h=${kaartView.hoogte}` : '';
      if (isIOSPlatform()) { const r = await API.get(`/routes/ritten/${rit.id}/pdf?mode=token${v}`); openUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`); return; }
      const r = await API.get(`/routes/ritten/${rit.id}/pdf?x=1${v}`, { responseType: 'blob' });
      openUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error('PDF mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const ritWhatsApp = async (rit) => {
    try {
      let tel = rit.chauffeur_tel || '';
      if (!tel) { tel = window.prompt('GSM-nummer van de chauffeur (bv. 0470 12 34 56):', '') || ''; if (!tel) return; }
      const r = await API.post(`/routes/ritten/${rit.id}/whatsapp-link`, { tel });
      openUrl(r.data.url);
    } catch (e) { toast.error('WhatsApp-link mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const ritLink = async (rit) => {
    const url = `${window.location.origin}/rit/${rit.token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Chauffeur-link gekopieerd — werkt zonder login op elke telefoon'); } catch { window.prompt('Chauffeur-link:', url); }
  };
  const ritVerwijder = async (rit) => {
    if (!window.confirm(`Rit van ${rit.chauffeur_naam || 'deze chauffeur'} annuleren? Open leverbonnen krijgen hun vorige status terug.`)) return;
    try { await API.delete(`/routes/ritten/${rit.id}`); toast.success('Rit geannuleerd'); laad(datum); }
    catch (e) { toast.error('Annuleren mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const stopStatus = async (rit, docId, status) => {
    try { await API.put(`/routes/ritten/${rit.id}/stops/${docId}`, { status }); laad(datum); }
    catch (e) { toast.error('Status wijzigen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  // Nadien nog een leverbon bijzetten in een al vastgelegde rit (ook als er nog geen factuur is)
  const bijzetKandidaten = useMemo(() => [
    ...(data.stops || []).map(s => ({ doc_id: s.doc_id, documentnr: s.documentnr, klant_naam: s.klant_naam, leverdatum: s.leverdatum, status: s.status })),
    ...(data.andere_open || []).map(a => ({ doc_id: a.id, documentnr: a.documentnr, klant_naam: a.klant_naam, leverdatum: a.leverdatum, status: a.status })),
  ], [data.stops, data.andere_open]);

  const ritBijzetten = async (rit, docId) => {
    if (!docId) return;
    setBezig('bijzet');
    try {
      const r = await API.post(`/routes/ritten/${rit.id}/bijzetten`, { doc_id: docId });
      const b = r.data.bijgezet || {};
      toast.success(`${b.documentnr} (${b.klant_naam}) bijgezet op plek ${b.positie} — ETA's en km herberekend`);
      await laad(datum);
      setActieveRit(r.data);
    } catch (e) { toast.error('Bijzetten mislukt: ' + (e.response?.data?.detail || e.message)); }
    finally { setBezig(''); }
  };
  const voegBij = async (docId) => {
    if (!docId) return;
    try {
      const r = await API.post('/routes/stops', { doc_ids: [docId] });
      const nieuw = (r.data.stops || [])[0];
      if (!nieuw) return;
      setData(d => ({ ...d, stops: [...d.stops.filter(s => s.doc_id !== docId), nieuw], andere_open: d.andere_open.filter(a => a.id !== docId) }));
      setGekozen(g => new Set([...g, docId]));
      setBijvoeg('');
      if (!nieuw.geo) geocodeer([docId], false);
    } catch (e) { toast.error('Bijvoegen mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const instOpslaan = async (body) => {
    try {
      const r = await API.put('/routes/instellingen', body);
      setData(d => ({ ...d, instellingen: r.data }));
      setForm(f => ({ ...f, vertrekUur: r.data.vertrek_uur, stoptijd: r.data.stoptijd_min, roundtrip: r.data.roundtrip }));
      setInstOpen(false);
      toast.success('Instellingen opgeslagen');
      if (!r.data.depot_geo) geocodeer([], true);
    } catch (e) { toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  const inst = data.instellingen;
  const depot = inst ? { ...inst.depot, geo: inst.depot_geo } : null;
  const kaartStops = planning ? planning.stops : actieveRit ? actieveRit.stops : gekozenStops;
  const kaartGeometry = planning ? planning.geometry : actieveRit ? actieveRit.geometry : null;
  const zonderGeo = gekozenStops.filter(s => !s.geo).length;

  return (
    <div className="p-4 md:p-6" data-testid="routeplanning-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2"><MapTrifold size={28} weight="duotone" className="text-[#F59E0B]" /> Routeplanning</h1>
          <p className="text-sm text-gray-500 mt-1">Open leverbonnen per dag → kortste route → ritblad, WhatsApp en chauffeur-weergave.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn px-2" onClick={() => setDatum(d => schuifDag(d, -1))} data-testid="route-dag-vorige"><CaretLeft size={16} /></button>
          <input type="date" className="field-input" style={{ width: 160 }} value={datum} onChange={e => e.target.value && setDatum(e.target.value)} data-testid="route-datum" />
          <button type="button" className="btn px-2" onClick={() => setDatum(d => schuifDag(d, 1))} data-testid="route-dag-volgende"><CaretRight size={16} /></button>
          <button type="button" className="btn text-xs" onClick={() => setDatum(vandaagISO())} data-testid="route-vandaag">Vandaag</button>
          <button type="button" className="btn inline-flex items-center gap-1 text-xs" onClick={() => setInstOpen(o => !o)} data-testid="route-instellingen-toggle"><GearSix size={15} /> Instellingen</button>
        </div>
      </div>
      {instOpen && inst && <InstellingenPaneel inst={inst} onOpslaan={instOpslaan} onSluit={() => setInstOpen(false)} />}
      <div className="text-sm font-semibold text-gray-700 mb-3 capitalize" data-testid="route-datum-label">{fmtDatum(datum)}</div>

      <div className="grid grid-cols-1 xl:grid-cols-[460px_1fr] gap-4">
        <div className="space-y-4">
          <div className="border border-gray-200 bg-white p-3" data-testid="route-rit-opties">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2"><Lbl>Chauffeur</Lbl>
                <select className="field-input" value={form.chauffeurId} onChange={e => setForm(f => ({ ...f, chauffeurId: e.target.value }))} data-testid="route-chauffeur">
                  <option value="">— geen / later —</option>
                  {chauffeurs.map(c => <option key={c.id} value={c.id}>{c.naam}{c.nummerplaat ? ` (${c.nummerplaat})` : ''}</option>)}
                </select>
                {!chauffeurs.length && <div className="text-[10px] text-gray-400 mt-0.5">Chauffeurs beheer je in <Link to="/instellingen?tab=chauffeurs" className="underline">Instellingen → Chauffeurs</Link>.</div>}
              </div>
              <div><Lbl>Vertrek</Lbl><input type="time" className="field-input" value={form.vertrekUur} onChange={e => setForm(f => ({ ...f, vertrekUur: e.target.value }))} data-testid="route-vertrek" /></div>
              <div><Lbl>Stoptijd (min)</Lbl><input type="number" min="0" className="field-input" value={form.stoptijd} onChange={e => setForm(f => ({ ...f, stoptijd: parseInt(e.target.value, 10) || 0 }))} data-testid="route-stoptijd" /></div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={form.roundtrip} onChange={e => setForm(f => ({ ...f, roundtrip: e.target.checked }))} data-testid="route-roundtrip" /> Terug naar vertrekpunt</label>
              <div className="text-[11px] text-gray-500 truncate max-w-[260px]" title={depot?.tekst}>
                {depot?.geo ? <span className="text-emerald-700">● Vertrek: {depot.tekst}</span> : <span className="text-amber-700">● Vertrekpunt nog niet op kaart{depot?.tekst ? ` (${depot.tekst})` : ''}</span>}
              </div>
            </div>
          </div>

          <div className="border border-gray-200 bg-white" data-testid="route-stops-paneel">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between gap-2">
              <div className="text-sm font-bold text-gray-900">Te plannen leveringen <span className="text-gray-400 font-normal">({gekozenStops.length}/{data.stops.length})</span></div>
              <div className="flex items-center gap-2 text-[11px]">
                <button type="button" className="text-[#002FA7] hover:underline" onClick={() => setGekozen(new Set(data.stops.map(s => s.doc_id)))}>alles</button>
                <button type="button" className="text-gray-500 hover:underline" onClick={() => setGekozen(new Set())}>niets</button>
              </div>
            </div>
            {geoProgress && (
              <div className="px-3 py-1.5 bg-amber-50 text-[11px] text-amber-800 border-b border-amber-100" data-testid="route-geo-progress">
                ⏳ Adressen opzoeken op de kaart… {geoProgress.done}/{geoProgress.total}
                <div className="h-1 bg-amber-100 mt-1"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${geoProgress.total ? Math.round((geoProgress.done / geoProgress.total) * 100) : 100}%` }} /></div>
              </div>
            )}
            <div className="max-h-[380px] overflow-y-auto">
              {bezig === 'laden' && !data.stops.length && <div className="p-4 text-sm text-gray-400">Laden…</div>}
              {bezig !== 'laden' && !data.stops.length && <div className="p-4 text-sm text-gray-500" data-testid="route-geen-stops">Geen open leverbonnen met leverdatum {datum.split('-').reverse().join('/')}. Voeg hieronder leverbonnen van een andere dag toe.</div>}
              {data.stops.map(s => <StopRij key={s.doc_id} s={s} gekozen={gekozen.has(s.doc_id)} toggle={() => toggle(s.doc_id)} onHergeocode={() => geocodeer([s.doc_id], false)} />)}
            </div>
            <div className="px-3 py-2 border-t border-gray-200 flex items-center gap-2">
              <Plus size={14} className="text-gray-400 shrink-0" />
              <select className="field-input text-xs flex-1" value={bijvoeg} onChange={e => voegBij(e.target.value)} data-testid="route-bijvoegen">
                <option value="">Leverbon van een andere dag bijvoegen… ({data.andere_open.length})</option>
                {data.andere_open.map(a => <option key={a.id} value={a.id}>{a.documentnr} · {a.klant_naam} · {a.leverdatum ? a.leverdatum.split('-').reverse().join('/') : 'geen datum'}</option>)}
              </select>
            </div>
            <div className="px-3 py-2 border-t border-gray-200">
              <button type="button" className="btn btn-primary w-full inline-flex items-center justify-center gap-2" style={{ background: '#F59E0B', borderColor: '#F59E0B' }} disabled={!!bezig || !gekozenStops.length || zonderGeo > 0 || !depot?.geo} onClick={() => optimaliseer()} data-testid="route-optimaliseer">
                <Path size={16} weight="bold" /> {bezig === 'optimaliseer' ? 'Route berekenen…' : `Optimaliseer route (${gekozenStops.length} stops)`}
              </button>
              {zonderGeo > 0 && <div className="text-[10px] text-amber-700 mt-1">{zonderGeo} aangevinkte levering(en) zonder kaartpositie — vink uit of corrigeer het adres bij de klant.</div>}
            </div>
          </div>

          {planning && (
            <div className="border-2 border-[#F59E0B] bg-white" data-testid="route-planning">
              <div className="px-3 py-2 border-b border-gray-200 bg-amber-50">
                <div className="text-sm font-bold text-gray-900">Voorgestelde route</div>
                <div className="text-[11px] text-gray-700 flex flex-wrap gap-x-3" data-testid="route-planning-totalen">
                  <span><b>{fmtKm(planning.totaal_afstand_m)}</b> totaal</span>
                  <span><b>{fmtDuur(planning.rij_duur_s)}</b> rijden</span>
                  <span><b>{fmtDuur(planning.totaal_duur_s)}</b> incl. stops</span>
                  <span>{planning.vertrek_uur} → <b>{planning.eind_uur}</b></span>
                </div>
              </div>
              <div className="max-h-[360px] overflow-y-auto">
                {planning.stops.map((s, i) => (
                  <div key={s.doc_id} className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 text-xs" data-testid={`route-planning-stop-${i}`}>
                    <span className="w-6 h-6 rounded-full bg-[#F59E0B] text-white font-bold flex items-center justify-center text-[11px] shrink-0">{s.positie}</span>
                    <span className="font-mono font-bold text-gray-800 w-11 shrink-0">{s.eta}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{s.klant_naam} <span className="font-mono text-[10px] text-gray-400">{s.documentnr}</span></div>
                      <div className="text-[10px] text-gray-500 truncate">{s.adres?.tekst} · +{Math.round(s.duur_s / 60)} min · {fmtKm(s.afstand_m)}</div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button type="button" className="p-1 text-gray-400 hover:text-gray-800 disabled:opacity-30" disabled={i === 0 || !!bezig} onClick={() => verplaats(i, -1)} title="Vroeger"><ArrowUp size={13} /></button>
                      <button type="button" className="p-1 text-gray-400 hover:text-gray-800 disabled:opacity-30" disabled={i === planning.stops.length - 1 || !!bezig} onClick={() => verplaats(i, 1)} title="Later"><ArrowDown size={13} /></button>
                      <button type="button" className="p-1 text-gray-400 hover:text-red-600" disabled={!!bezig} onClick={() => haalUitPlanning(s.doc_id)} title="Uit deze rit halen"><Trash size={13} /></button>
                    </div>
                  </div>
                ))}
                {planning.terug && <div className="px-3 py-1.5 text-[11px] text-gray-600 bg-gray-50">↩ Terug op vertrekpunt ± <b>{planning.terug.eta}</b> · {fmtKm(planning.terug.afstand_m)}</div>}
              </div>
              <div className="p-3 border-t border-gray-200 flex gap-2">
                <button type="button" className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2" disabled={!!bezig} onClick={vastleggen} data-testid="route-vastleggen"><Truck size={16} /> {bezig === 'opslaan' ? 'Vastleggen…' : 'Rit vastleggen'}</button>
                <button type="button" className="btn" disabled={!!bezig} onClick={() => setPlanning(null)}>Annuleer</button>
              </div>
              <div className="px-3 pb-2 text-[10px] text-gray-500">Bij vastleggen krijgen de leverbonnen status <b>onderweg</b>; daarna kan je het ritblad printen of via WhatsApp naar de chauffeur sturen.</div>
            </div>
          )}

          <div data-testid="route-ritten">
            <div className="text-sm font-bold text-gray-900 mb-2">Ritten op deze dag <span className="text-gray-400 font-normal">({data.ritten.length})</span></div>
            {!data.ritten.length && <div className="text-xs text-gray-400 border border-dashed border-gray-300 p-3">Nog geen rit vastgelegd voor deze dag.</div>}
            <div className="space-y-2">
              {data.ritten.map(r => <RitKaart key={r.id} rit={r} live={live.voertuigen.find(v => v.rit_id === r.id)} actief={actieveRit?.id === r.id} kandidaten={bijzetKandidaten} bezig={bezig} onToon={() => { setActieveRit(a => a?.id === r.id ? null : r); setPlanning(null); }} onPdf={() => ritPdf(r)} onWhatsApp={() => ritWhatsApp(r)} onLink={() => ritLink(r)} onVerwijder={() => ritVerwijder(r)} onStopStatus={(docId, st) => stopStatus(r, docId, st)} onBijzetten={(docId) => ritBijzetten(r, docId)} />)}
            </div>
          </div>
        </div>

        <div>
          <RouteKaart depot={depot} stops={kaartStops} geometry={kaartGeometry} kleur={actieveRit ? '#0891B2' : '#F59E0B'} hoogte={640} voertuigen={actieveRit ? live.voertuigen.filter(v => v.rit_id === actieveRit.id) : live.voertuigen} spoor={actieveRit ? spoor : []} onView={setKaartView} />
          <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-gray-500">
            <span><span className="inline-block w-3 h-3 rounded-full bg-gray-900 align-middle mr-1" />Vertrekpunt</span>
            <span><span className="inline-block w-3 h-3 rounded-full bg-[#F59E0B] align-middle mr-1" />Stop (nummer = volgorde)</span>
            <span><span className="inline-block w-3 h-3 rounded-full bg-[#0891B2] align-middle mr-1" />Vastgelegde rit / wagen live</span>
            <span><span className="inline-block w-3 h-3 rounded-full bg-emerald-600 align-middle mr-1" />Geleverd</span>
            <span className="font-semibold text-cyan-800" data-testid="route-live-samenvatting">{live.voertuigen.filter(v => v.tracking_actief).length} wagen(s) live</span>
            <span className="ml-auto">Kaart & routes: OpenStreetMap / OSRM · positie via telefoon chauffeur</span>
          </div>
        </div>
      </div>
    </div>
  );
}
